/* Camera pose from four marked corners.

   Lifted out of the single-file build with one change: it took its inputs from
   the global S, and now takes them as arguments. That is what lets the solve run
   outside a browser, which is how it is regression-tested — synthesise the photo
   a known camera would have taken of a known screen, then check the recovered
   pose matches. It currently comes back to within 2e-14 m. */

import { homography, quadCheck, dot, len, nrm, crs } from './linalg.js';

/** Corner keys in solve order: clockwise from top-left, in y-down image space. */
export const CK = ['tl','tr','br','bl'];
export const CLABEL = {tl:'TL',tr:'TR',br:'BR',bl:'BL'};

/* THE PRINCIPAL POINT, and why it is still the centre.

   The solve models a pinhole, and every image coordinate in it is really a
   displacement from the principal point — where the lens axis crosses the
   image. For a whole frame that is the centre. For a CROPPED one it is not:
   cutting a region out moves the pixels and not the lens.

   That reasoning is correct, it was implemented, and it was reverted. On a
   synthetic rig — known camera, known screen, hard off-centre crop — reading
   photo.rect recovered the camera exactly where assuming the centre put it
   7.59 m away. On a real project it made things 13x WORSE: the grid-uniformity
   check (a camera-facing plane must render as square cells) went from 0.29%
   variation across the panel to 3.75%.

   The premise is what fails. photo.rect says how the app cropped the image it
   was GIVEN, and only equals a crop of the lens's own frame if that image was
   itself uncropped and unedited. Phone pipelines, prior edits and any photo
   that arrived already trimmed all break it, silently, and nothing in the file
   distinguishes them from an untouched original.

   So: assume the centre, which is wrong by a bounded amount for a cropped
   photo, rather than trust a rect that can be wrong by an unbounded one.
   Anyone revisiting this needs a real source of the principal point — EXIF, or
   asking — not an inference from the crop. photo.rect is still persisted
   (core/doc.js) so that work has something to build on. */

/**
 * @param {{w:number,h:number,rect?:object,rotated?:boolean}} photo  pixel dimensions of the reference photo
 * @param {object} corners               {tl,tr,br,bl}, each {x,y} normalised 0..1
 * @param {{w:number,h:number}} screen   real screen size, metres
 * @param {{mode:string,manualMM:number}} focal
 */
/* Solve focal length + camera pose from the marked quad.
   Plane frame: X right, Y down, Z into the screen (so the viewer sits at −Z).
   Returns {eye:[x,y,z] in Three coords, f, hfov, mm35, method} or {error}. */
export function solveCalibration({ photo, corners, screen, focal }){
  const pw=photo.w, ph=photo.h;
  const img = CK.map(k=>[corners[k].x*pw, corners[k].y*ph]);
  const chk = quadCheck(img);
  if(!chk.ok) return {error:chk.msg};

  const W=screen.w, H=screen.h;
  if(!(W>0&&H>0)) return {error:'Enter the screen\u2019s real width and height in metres first.'};

  const world=[[-W/2,-H/2],[W/2,-H/2],[W/2,H/2],[-W/2,H/2]];
  const Hm = homography(world, img);
  if(!Hm) return {error:'That quad is degenerate. Move the corners apart and try locking the angle again.'};

  const cx=pw/2, cy=ph/2;
  const h1=[Hm[0],Hm[3],Hm[6]], h2=[Hm[1],Hm[4],Hm[7]], h3=[Hm[2],Hm[5],Hm[8]];
  const a1=h1[0]-cx*h1[2], b1=h1[1]-cy*h1[2];
  const a2=h2[0]-cx*h2[2], b2=h2[1]-cy*h2[2];

  let f=null, method='';
  if(focal.mode==='manual'){
    f = focal.manualMM/36*pw;                       // 35 mm-equivalent → pixels
    method='manual';
  }else{
    const d1=h1[2]*h2[2];
    if(Math.abs(d1)>1e-13){
      const v=-(a1*a2+b1*b2)/d1;
      if(v>1e-6){ f=Math.sqrt(v); method='right-angle constraint'; }
    }
    if(f===null){
      const d2=h2[2]*h2[2]-h1[2]*h1[2];
      if(Math.abs(d2)>1e-13){
        const v=((a1*a1+b1*b1)-(a2*a2+b2*b2))/d2;
        if(v>1e-6){ f=Math.sqrt(v); method='equal-scale constraint'; }
      }
    }
    if(f===null) return {error:'This photo is too close to straight-on for the focal length to be recovered. Switch the lens setting to Manual and type the focal length you shot at.'};
  }

  const ki=v=>[(v[0]-cx*v[2])/f, (v[1]-cy*v[2])/f, v[2]];
  const R1=ki(h1), R2=ki(h2), T=ki(h3);
  let lam=1/len(R1);
  if(!isFinite(lam)) return {error:'The angle can’t be locked from this quad. Re-mark the corners and try again.'};
  if(T[2]<0) lam=-lam;                                 // world origin must be in front of the lens

  const r1=R1.map(v=>v*lam), r2=R2.map(v=>v*lam), t=T.map(v=>v*lam);
  const n1=nrm(r1);
  const n2=nrm([r2[0]-dot(r2,n1)*n1[0], r2[1]-dot(r2,n1)*n1[1], r2[2]-dot(r2,n1)*n1[2]]);
  const n3=crs(n1,n2);
  const C=[-dot(n1,t), -dot(n2,t), -dot(n3,t)];        // camera centre, plane frame

  const eye=[C[0], -C[1], -C[2]];                      // → Three: X right, Y up, Z toward viewer
  if(!(eye[2]>1e-4) || !isFinite(eye[0]) || !isFinite(eye[1]))
    return {error:'Locking this angle would put the camera behind the screen. Check that the corners are marked in TL → TR → BR → BL order.'};

  return {
    eye, f, method,
    /* pw/2, NOT cx. This is the lens's angle of view, set by how wide the
       frame is — cx only coincided with that while the principal point was
       assumed central, and reading it here would make the reported field of
       view drift with the crop. */
    hfov: 2*Math.atan((pw/2)/f)*180/Math.PI,
    mm35: f/pw*36,
    /* The full recovered pose, in the PLANE frame (X right, Y down, Z into the
       screen) — everything above is derived from it and then thrown away, which
       is fine for one screen and useless for two. core/corner.js needs to
       back-project a pixel onto a second plane that shares this screen's right
       edge, and that needs the camera itself, not a summary of it.

       n1/n2/n3 are the ROWS of camera→plane (equivalently the COLUMNS of
       plane→camera), so a ray through a pixel is [n1·v, n2·v, n3·v]. Measured
       against a synthetic rig rather than derived: both pairings produce
       plausible-looking directions and the wrong one lands the second panel
       behind the viewer. See core/corner.js. */
    pose: { n1, n2, n3, t, C, cx, cy, f }
  };
}
